import { ReviewContext } from 'components/review/ReviewContextProvider';
import { getTheme, mergeStyleSets, Stack, Checkbox, IconButton, Modal, TooltipHost, Text } from '@fluentui/react';
import React, { useContext, useEffect, useState } from 'react';
import { Case, QueueConfiguration, SubjectImagePreferences } from 'services';
import { CaseContent } from '../CaseContent';
import { scrollStyle } from '../content/JsonFrame';
import { ReviewTimer } from '../ReviewTimer';

const theme = getTheme();

const dialogShadow = '0px 6px 12px 0px rgba(0, 0, 0, 0.08)';
const jsonDialogStyles = mergeStyleSets({
  root: {
    minHeight: 500,
    maxHeight: '100%',
    selectors: scrollStyle,
    boxShadow: dialogShadow
  }
});

const backIconStyles = mergeStyleSets({
  root: {
    height: '42px',
    width: '42px',
    marginRight: '24px',
    background: theme.palette.neutralDark
  },
  rootHovered: {
    background: theme.palette.neutralSecondary
  },
  rootPressed: {
    background: theme.palette.neutralSecondaryAlt
  },
  icon: {
    fontSize: 'large',
    color: theme.palette.neutralLight
  }
});

const forwardIconStyles = mergeStyleSets({
  root: {
    height: '42px',
    width: '42px',
    marginLeft: '24px',
    background: theme.palette.neutralDark
  },
  rootHovered: {
    background: theme.palette.neutralSecondary
  },
  rootPressed: {
    background: theme.palette.neutralSecondaryAlt
  },
  icon: {
    fontSize: 'large',
    color: theme.palette.neutralLight
  }
});

export interface ZoomModalProps {
  isOpen: boolean;
  config: QueueConfiguration;
  settings: SubjectImagePreferences;
  case?: Case;
  caseCard?: number;
  isChecked?: boolean;
  timeLeftInSeconds?: number;
  showCheckbox: boolean;
  onDismiss: () => void;
  setIsChecked?: (boolean) => void;
  onBackClick?: () => void;
  onNextClick?: () => void;
}

export const ZoomModal: React.FunctionComponent<ZoomModalProps> = props => {
  const { palette } = getTheme();
  const { dispatch } = useContext(ReviewContext);
  const [showModal, setShowModal] = useState<boolean>(false);

  useEffect(() => {
    setShowModal(props.isOpen);
  }, [props.isOpen]);

  return (
    <div id="ZoomContainer">
      <Modal
        titleAriaId="modalLabel"
        styles={{
          main: { width: '70%', height: 'max-content' },
          root: { position: 'absolute', paddingTop: 78 }
        }}
        isOpen={showModal}
        onDismiss={() => setShowModal(false)}
        onDismissed={props.onDismiss}
      >
        <Text id="modalLabel" styles={{ root: { display: 'none' } }}>
          {props.caseCard ? `Case card ${props.caseCard} zoom` : 'Zoom'}
        </Text>
        {props.onBackClick && (
          <Stack.Item>
            <IconButton
              id="ZoomBack"
              styles={backIconStyles}
              onClick={(event): void => {
                event.stopPropagation();
                props.onBackClick && props.onBackClick();
              }}
              iconProps={{ iconName: 'ChevronLeft' }}
            />
          </Stack.Item>
        )}
        {props.case && (
          <Stack.Item className={'h-75 overflow-overlay rounded'} align="center" styles={jsonDialogStyles}>
            <div
              className="position-relative h-100 w-100"
              style={{
                background: palette.white,
                borderRadius: 6,
                paddingTop: 10,
                paddingBottom: 5,
                color: palette.black
              }}
              onClick={(event): void => event.stopPropagation()}
            >
              <Stack
                horizontal
                horizontalAlign={props.timeLeftInSeconds ? 'space-between' : 'end'}
                className="px-3 py-1"
              >
                {props.timeLeftInSeconds && (
                  <Stack.Item>
                    <ReviewTimer timeLeftInSeconds={props.timeLeftInSeconds} />
                  </Stack.Item>
                )}
                <Stack.Item>
                  {props.showCheckbox ? (
                    <TooltipHost content={'Select case and close modal'}>
                      <Checkbox
                        checked={props.isChecked}
                        ariaLabel={'Select case and close modal'}
                        onChange={(ev, checked?: boolean) => {
                          props.setIsChecked && props.setIsChecked(checked);
                          const actionType = checked ? 'SELECT_CASE' : 'UNSELECT_CASE';
                          if (props.case && dispatch) {
                            dispatch({ type: actionType, payload: [props.case] });
                          }
                          setShowModal(false);
                        }}
                      />
                    </TooltipHost>
                  ) : (
                    <IconButton
                      iconProps={{ iconName: 'cancel' }}
                      onClick={() => setShowModal(false)}
                      ariaLabel={'Close modal'}
                      styles={{
                        rootHovered: {
                          color: palette.neutralDark
                        }
                      }}
                    />
                  )}
                </Stack.Item>
              </Stack>
              <CaseContent case={props.case} config={props.config} isZoomInActive={true} className="opacity-1" />
            </div>
          </Stack.Item>
        )}
        {props.onNextClick && (
          <Stack.Item>
            <IconButton
              styles={forwardIconStyles}
              onClick={(event): void => {
                event.stopPropagation();
                props.onNextClick && props.onNextClick();
              }}
              iconProps={{ iconName: 'ChevronRight' }}
            />
          </Stack.Item>
        )}
      </Modal>
    </div>
  );
};
